import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth, AuthContext } from './AuthContext';
import { CivicLevel } from './civicLevelTypes';

interface AdminContextType {
  isAdmin: boolean;
  adminMode: boolean;
  viewAsUser: boolean;
  previousRoute: string | null;
  previousCardId: string | null;
  previewCivicLevel: CivicLevel;
  previewLoggedIn: boolean;
  previewIncludeDrafts: boolean;
  enterAdminMode: (fromRoute?: string, fromCardId?: string) => void;
  exitAdminMode: () => void;
  toggleViewAsUser: () => void;
  setPreviewCivicLevel: (level: CivicLevel) => void;
  setPreviewLoggedIn: (loggedIn: boolean) => void;
  setPreviewIncludeDrafts: (includeDrafts: boolean) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

const ADMIN_MODE_KEY = 'civicbeat_admin_mode';
const VIEW_AS_USER_KEY = 'civicbeat_view_as_user';
const PREVIOUS_ROUTE_KEY = 'civicbeat_previous_route';
const PREVIOUS_CARD_ID_KEY = 'civicbeat_previous_card_id';
const PREVIEW_CIVIC_LEVEL_KEY = 'civicbeat_preview_civic_level';
const PREVIEW_LOGGED_IN_KEY = 'civicbeat_preview_logged_in';
const PREVIEW_INCLUDE_DRAFTS_KEY = 'civicbeat_preview_include_drafts';

// Use sessionStorage for preview settings so they persist during session but not across browser restarts
const previewStorage = typeof window !== 'undefined' ? sessionStorage : null;

export function AdminProvider({ children }: { children: ReactNode }) {
  const authContext = useContext(AuthContext);
  const { profile } = authContext || { profile: null };
  const userIsAdmin = profile?.is_admin === true;

  console.log('[AdminContext] Profile check:', {
    hasProfile: !!profile,
    profileId: profile?.id,
    role: profile?.role,
    is_admin: profile?.is_admin,
    userIsAdmin
  });

  const [adminMode, setAdminMode] = useState(() => {
    if (!userIsAdmin) return false;
    const stored = localStorage.getItem(ADMIN_MODE_KEY);
    return stored === 'true';
  });

  const [viewAsUser, setViewAsUser] = useState(() => {
    if (!userIsAdmin || !adminMode) return false;
    const stored = localStorage.getItem(VIEW_AS_USER_KEY);
    return stored === 'true';
  });

  const [previousRoute, setPreviousRoute] = useState<string | null>(() => {
    if (!userIsAdmin || !adminMode) return null;
    return localStorage.getItem(PREVIOUS_ROUTE_KEY);
  });

  const [previousCardId, setPreviousCardId] = useState<string | null>(() => {
    if (!userIsAdmin || !adminMode) return null;
    const stored = localStorage.getItem(PREVIOUS_CARD_ID_KEY);
    console.log('[AdminContext] Initializing previousCardId from localStorage:', stored);
    return stored;
  });

  const [previewCivicLevel, setPreviewCivicLevelState] = useState<CivicLevel>(() => {
    if (!userIsAdmin || !adminMode) return 'Observer';
    const stored = previewStorage?.getItem(PREVIEW_CIVIC_LEVEL_KEY);
    return (stored as CivicLevel) || 'Observer';
  });

  const [previewLoggedIn, setPreviewLoggedInState] = useState(() => {
    if (!userIsAdmin || !adminMode) return true;
    const stored = previewStorage?.getItem(PREVIEW_LOGGED_IN_KEY);
    return stored === null ? true : stored === 'true';
  });

  const [previewIncludeDrafts, setPreviewIncludeDraftsState] = useState(() => {
    if (!userIsAdmin || !adminMode) return false;
    const stored = previewStorage?.getItem(PREVIEW_INCLUDE_DRAFTS_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    if (!userIsAdmin) {
      setAdminMode(false);
      setViewAsUser(false);
      setPreviousRoute(null);
      setPreviousCardId(null);
      setPreviewCivicLevelState('Observer');
      setPreviewLoggedInState(true);
      setPreviewIncludeDraftsState(false);
      localStorage.removeItem(ADMIN_MODE_KEY);
      localStorage.removeItem(VIEW_AS_USER_KEY);
      localStorage.removeItem(PREVIOUS_ROUTE_KEY);
      localStorage.removeItem(PREVIOUS_CARD_ID_KEY);
      previewStorage?.removeItem(PREVIEW_CIVIC_LEVEL_KEY);
      previewStorage?.removeItem(PREVIEW_LOGGED_IN_KEY);
      previewStorage?.removeItem(PREVIEW_INCLUDE_DRAFTS_KEY);
      clearUrlParams();
    }
  }, [userIsAdmin]);

  useEffect(() => {
    if (viewAsUser) {
      updateUrlParams();
    } else {
      clearUrlParams();
    }
  }, [viewAsUser, previewCivicLevel, previewLoggedIn, previewIncludeDrafts]);

  const updateUrlParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('preview', '1');
    url.searchParams.set('auth', previewLoggedIn ? 'logged_in' : 'logged_out');
    url.searchParams.set('level', previewCivicLevel.toLowerCase().replace(/\s+/g, '-'));
    if (previewIncludeDrafts) {
      url.searchParams.set('drafts', '1');
    }
    window.history.replaceState({}, '', url);
  };

  const clearUrlParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    url.searchParams.delete('auth');
    url.searchParams.delete('level');
    url.searchParams.delete('drafts');
    window.history.replaceState({}, '', url);
  };

  const enterAdminMode = (fromRoute?: string, fromCardId?: string) => {
    if (!userIsAdmin) return;
    console.log('[AdminContext] enterAdminMode called with:', { fromRoute, fromCardId });
    setAdminMode(true);
    if (fromRoute) {
      setPreviousRoute(fromRoute);
      localStorage.setItem(PREVIOUS_ROUTE_KEY, fromRoute);
    }
    if (fromCardId) {
      setPreviousCardId(fromCardId);
      localStorage.setItem(PREVIOUS_CARD_ID_KEY, fromCardId);
      console.log('[AdminContext] Saved previousCardId to localStorage:', fromCardId);
    } else {
      setPreviousCardId(null);
      localStorage.removeItem(PREVIOUS_CARD_ID_KEY);
      console.log('[AdminContext] No cardId to save, cleared localStorage');
    }
    localStorage.setItem(ADMIN_MODE_KEY, 'true');
  };

  const exitAdminMode = () => {
    setAdminMode(false);
    setViewAsUser(false);
    setPreviewCivicLevelState('Observer');
    setPreviewLoggedInState(true);
    setPreviewIncludeDraftsState(false);
    localStorage.removeItem(ADMIN_MODE_KEY);
    localStorage.removeItem(VIEW_AS_USER_KEY);
    previewStorage?.removeItem(PREVIEW_CIVIC_LEVEL_KEY);
    previewStorage?.removeItem(PREVIEW_LOGGED_IN_KEY);
    previewStorage?.removeItem(PREVIEW_INCLUDE_DRAFTS_KEY);
    const route = previousRoute;
    setPreviousRoute(null);
    setPreviousCardId(null);
    localStorage.removeItem(PREVIOUS_ROUTE_KEY);
    localStorage.removeItem(PREVIOUS_CARD_ID_KEY);
    return route;
  };

  const toggleViewAsUser = () => {
    if (!adminMode) return;
    const newValue = !viewAsUser;
    setViewAsUser(newValue);
    localStorage.setItem(VIEW_AS_USER_KEY, newValue.toString());
  };

  const setPreviewCivicLevel = (level: CivicLevel) => {
    if (!adminMode) return;
    setPreviewCivicLevelState(level);
    previewStorage?.setItem(PREVIEW_CIVIC_LEVEL_KEY, level);
  };

  const setPreviewLoggedIn = (loggedIn: boolean) => {
    if (!adminMode) return;
    setPreviewLoggedInState(loggedIn);
    previewStorage?.setItem(PREVIEW_LOGGED_IN_KEY, loggedIn.toString());
  };

  const setPreviewIncludeDrafts = (includeDrafts: boolean) => {
    if (!adminMode) return;
    setPreviewIncludeDraftsState(includeDrafts);
    previewStorage?.setItem(PREVIEW_INCLUDE_DRAFTS_KEY, includeDrafts.toString());
  };

  const value: AdminContextType = {
    isAdmin: userIsAdmin,
    adminMode,
    viewAsUser,
    previousRoute,
    previousCardId,
    previewCivicLevel,
    previewLoggedIn,
    previewIncludeDrafts,
    enterAdminMode,
    exitAdminMode,
    toggleViewAsUser,
    setPreviewCivicLevel,
    setPreviewLoggedIn,
    setPreviewIncludeDrafts,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
